import React from 'react'
import ReactDOM from 'react-dom/client'
import { createBrowserRouter, RouterProvider } from 'react-router-dom'
import './util/polyfills'
import './index.css'
import { AuthProvider, TAuthConfig, TRefreshTokenExpiredEvent } from 'react-oauth2-code-pkce'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import * as Sentry from '@sentry/react'
import App from './App'
import ErrorPage from './pages/ErrorPage'
import ListPage from './pages/entity/ListPage'
import ExportPage from './pages/entity/ExportPage'
import './util/scope'
import EntityTypesPage from './pages/entity/EntityTypesPage'
import { PROD } from './config/stage'
import ImportPageRoute from './pages/entity/routes/ImportPageRoute'
import { ConfigurePageRoute } from './pages/entity/routes/ConfigurePageRoute'
import PermissionGrantsListPageRoute from './pages/permission/PermissionGrantsListPage'
import CreateOrEditPermissionGrantPageRoute from './pages/permission/CreateOrEditPermissionGrantPage'
import NewAccessControlPage from './pages/permission/NewAccessControlPage'
import ViewEntityPage from './pages/entity/ViewEntityPage'
import SignInPage from './pages/signin/SignInPage'
import SemanticsPageRoute from './pages/semantics/SemanticsPage'
import ViewEntityTypePage from './pages/entity/ViewEntityTypePage'
import { isDebugStage, logMessage } from './util/Logging'
import { debugNetwork, debugRemoveItem, debugSetItem } from './util/Debug'
import EditEntityRoute from './pages/entity/EditEntityRoute'
import NewEntityRoute from './pages/entity/NewEntityRoute'
import HierarchyRoute from './pages/hierarchies/HierarchyRoute'
import AssetRoute from './pages/assets/AssetRoute'
import AssetEmbedRoute from './pages/assets/AssetEmbedRoute'

const stage = import.meta.env.VITE_STAGE
const dsn = 'https://8dc821e5d3594e9c4eaabfe634b1dcf1@o232550.ingest.us.sentry.io/4508138519134208'

const logToSentry = !!import.meta.env.VITE_CI

Sentry.init({
  dsn: logToSentry ? dsn : '',
  integrations: [],
  environment: stage,
})

const authServiceStagePrefix = stage === PROD ? '' : 'test-'
const authServiceHost = `${authServiceStagePrefix}auth.eidu.com`

const authConfig: TAuthConfig = {
  clientId: 'eidu-console',
  authorizationEndpoint: `https://${authServiceHost}/realms/eidu/protocol/openid-connect/auth`,
  tokenEndpoint: `https://${authServiceHost}/realms/eidu/protocol/openid-connect/token`,
  redirectUri: window.location.origin,
  logoutEndpoint: `https://${authServiceHost}/realms/eidu/protocol/openid-connect/logout`,
  logoutRedirect: window.location.origin,
  scope: 'openid profile',
  preLogin: () => logMessage('preLogin', 'debug', 'AuthDebug'),
  postLogin: () => logMessage('postLogin', 'debug', 'AuthDebug'),
  onRefreshTokenExpire: (event: TRefreshTokenExpiredEvent) => {
    logMessage('onRefreshTokenExpire', 'debug', 'AuthDebug')
    event.logIn()
  },
}

if (isDebugStage()) {
  // Debugging authentication helpers
  debugSetItem('ROCP_')
  debugRemoveItem('ROCP_')
  debugNetwork('https://test-auth.eidu.com/')
}

// Hack to fix weird behaviour of react-oauth2-code-pkce
if (!window.location.search.includes('code=')) localStorage.setItem('ROCP_loginInProgress', 'false')

const signinRouter = createBrowserRouter([
  {
    path: '/signin',
    element: <SignInPage />,
    errorElement: <ErrorPage />,
  },
])

const mainRouter = createBrowserRouter([
  {
    path: '/',
    element: <App />,
    errorElement: <ErrorPage />,
    children: [
      {
        path: 'types',
        element: <EntityTypesPage />,
      },
      {
        path: 'types/new',
        element: <ConfigurePageRoute />,
      },
      {
        path: 'types/:entityTypeId',
        element: <ViewEntityTypePage />,
      },
      {
        path: 'types/:entityTypeId/edit',
        element: <ConfigurePageRoute />,
      },
      {
        path: 'entities/:entityTypeId',
        element: <ListPage />,
      },
      {
        path: 'entities/:entityTypeId/import',
        element: <ImportPageRoute />,
      },
      {
        path: 'entities/:entityTypeId/new',
        element: <NewEntityRoute />,
      },
      {
        path: 'entities/:entityTypeId/:entityId/edit',
        element: <EditEntityRoute />,
      },
      {
        path: 'hierarchies/:hierarchyId/:entityTypeId/:entityId/edit',
        element: <EditEntityRoute />,
      },
      {
        path: 'entities/:entityTypeId/:entityId',
        element: <ViewEntityPage />,
      },
      {
        path: 'hierarchies/:hierarchyId',
        element: <HierarchyRoute />,
      },
      {
        path: 'asset/:assetId',
        element: <AssetRoute />,
      },
      {
        path: 'asset-embed/:assetId',
        element: <AssetEmbedRoute />,
      },
      {
        path: 'export',
        element: <ExportPage />,
      },
      {
        path: 'access-control',
        element: <PermissionGrantsListPageRoute />,
      },
      {
        path: 'access-control-viewer',
        element: <NewAccessControlPage />,
      },
      {
        path: 'access-control/new',
        element: <CreateOrEditPermissionGrantPageRoute isEditMode={false} />,
      },
      {
        path: 'access-control/:permissionGrantId',
        element: <CreateOrEditPermissionGrantPageRoute isEditMode />,
      },

      {
        path: 'semantics',
        element: <SemanticsPageRoute />,
      },
    ],
  },
])

const isSignInPath = window.location.pathname === '/signin'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 0,
    },
  },
})

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    {isSignInPath ? (
      <RouterProvider router={signinRouter} />
    ) : (
      <AuthProvider authConfig={authConfig}>
        <QueryClientProvider client={queryClient}>
          <RouterProvider router={mainRouter} />
        </QueryClientProvider>
      </AuthProvider>
    )}
  </React.StrictMode>
)
